import {CommonModule} from '@angular/common';
import {HttpClient, HttpEventType} from '@angular/common/http';
import {Component, Input} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiService} from '../../core/http/api.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {environment} from '../../../environments/environment';

type ImportKind = 'users' | 'entities' | 'ratings' | 'entityLookup' | 'demographicCities';

interface ImportConfig {
  title: string;
  subtitle: string;
  columns: string;
  rules: string[];
  sampleUrl: string;
}

@Component({
  selector: 'app-import-data-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './import-data-modal.component.html',
  styleUrls: ['./import-data-modal.component.css'],
})
export class ImportDataModalComponent {
  @Input({required: true}) kind: ImportKind = 'users';
  visible = false;
  url = '';
  uploading = false;
  importing = false;
  progress = 0;

  readonly configs: Record<ImportKind, ImportConfig> = {
    users: {
      title: 'Import Users', subtitle: 'Import users from your file',
      columns: 'first_name, last_name, email, image, type, bio, phone, super_user',
      rules: ['The image column must contain an uploaded image URL.', 'Type must be bhakts or vishwaguru.', 'Super user must be Yes or No.'],
      sampleUrl: 'https://bhakts.s3.us-east-2.amazonaws.com/csv/users-sample-csv.csv',
    },
    entities: {
      title: 'Import Entities', subtitle: 'Import entities from your file',
      columns: 'first_name, last_name, category_id, date, label, image, in_person, allow_bhakt, allow_unbhakt, allow_rating, allow_story, allow_hypocrisy',
      rules: ['Category ID must match an existing category.', 'Date must use yyyy/dd/mm format.', 'Image must contain an uploaded image URL.', 'All allow fields and in_person must be Yes or No.'],
      sampleUrl: 'https://bhakts.s3.us-east-2.amazonaws.com/csv/entity-import-sample.csv',
    },
    ratings: {
      title: 'Import Ratings', subtitle: 'Import ratings from your file',
      columns: 'rating_category, entity_id, user_id, review, rating_params',
      rules: ['Rating category, entity and user IDs must already exist.', 'Rating params must contain three values in order, separated by commas.'],
      sampleUrl: 'https://bhakts.s3.us-east-2.amazonaws.com/csv/rating-sample-csv.csv',
    },
    entityLookup: {
      title: 'Import Entity Lookup', subtitle: 'Import entity lookup data from your file',
      columns: 'entity_id, lookup_ids',
      rules: ['Separate multiple lookup IDs with a pipe (|).'],
      sampleUrl: 'https://bhakts.s3.us-east-2.amazonaws.com/csv/1716545783547_lookup-update.csv',
    },
    demographicCities: {
      title: 'Import Demographic Clock Cities', subtitle: 'Import demographic clock cities from your file',
      columns: 'state, city, population, growth_rate_now, growth_rate_year_1, growth_rate_year_2, growth_rate_year_3, growth_rate_year_1_rate, growth_rate_year_2_rate, growth_rate_year_3_rate, hindu_population, muslim_population, christian_population, hindu_growth_rate, muslim_growth_rate, christian_growth_rate',
      rules: [],
      sampleUrl: 'https://bhakts.s3.us-east-2.amazonaws.com/csv/1726205610693_Cities_sample.csv',
    },
  };

  constructor(private http: HttpClient, private api: ApiService, private utils: UtilsService) {}

  get config(): ImportConfig { return this.configs[this.kind]; }

  open(): void { this.visible = true; this.reset(); }
  close(): void { if (!this.uploading && !this.importing) { this.visible = false; this.reset(); } }

  selectFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.uploading = true;
    const data = new FormData();
    data.append('file', file);
    this.http.post<any>('upload-document', data, {reportProgress: true, observe: 'events'}).subscribe({
      next: (res) => {
        if (res.type === HttpEventType.UploadProgress) this.progress = Math.round((res.loaded / (res.total || res.loaded)) * 100);
        if (res.type === HttpEventType.Response) { this.url = res.body.response.url; this.uploading = false; this.progress = 0; }
      },
      error: (err) => { this.uploading = false; this.progress = 0; this.utils.resErr(err); },
    });
  }

  submit(): void {
    if (!this.url || this.importing) return;
    this.importing = true;
    const payload = {url: this.url.replace(environment.s3BaseUrl, '')};
    this.request(payload).subscribe({
      next: () => { this.importing = false; this.utils.toastSuccess(`${this.config.title} completed successfully`); this.close(); },
      error: (err) => { this.importing = false; this.utils.resErr(err); },
    });
  }

  private request(payload: {url: string}): Observable<any> {
    if (this.kind === 'users') return this.api.importUsers(payload);
    if (this.kind === 'entities') return this.api.importEntities(payload);
    if (this.kind === 'ratings') return this.api.importRatings(payload);
    if (this.kind === 'entityLookup') return this.api.importEntityLookup(payload);
    return this.api.importDemographicClockCities(payload);
  }

  private reset(): void { this.url = ''; this.uploading = false; this.importing = false; this.progress = 0; }
}
